import {json, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import type {Page as PageType} from '@shopify/hydrogen/storefront-api-types';
import {useLoaderData, useLocation, useParams} from '@remix-run/react';
import invariant from 'tiny-invariant';
import {PageHeader} from '~/components';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {seoPayload} from '~/lib/seo.server';
import type {LinksFunction} from '@shopify/remix-oxygen';

//domains
import {usePageQuery} from 'domains/page';
import {
  getLangagueCodeForSanityQuery,
  getCountryCodeForSanityQuery,
} from '~/domains/sanity';
import {useBlogCategoriesPageQuery, useBlogCategoryQuery} from 'domains/blog';

//components
import {useIsHydrated} from '~/hooks/useIsHydrated';
import BlogListing from '~/components/blogListing';

//translation
import translation from 'translations/blog.json';
import {routeLocale} from 'domains/global';

export const headers = routeHeaders;

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({request, params, context}: LoaderArgs) {
  invariant(params.categoryHandle, 'Missing page handle');

  if(params.categoryHandle === 'all'){
    redirect(`/blogs`);
  }

  const searchParams = new URL(request.url).searchParams;
  const start = searchParams.get('start') ?? '0';
  const end = searchParams.get('end') ?? '8';

  const {currentCategory} = await useBlogCategoryQuery({
    languageCode: getLangagueCodeForSanityQuery(
      context.storefront.i18n.language,
    ),
    categoryHandle: params.categoryHandle,
  });

  // invariant(currentTag, `Tag doesn't exist`);
  if (!currentCategory) {
    throw new Response(null, {status: 404});
  }

  const {blogs, recentBlogs, tags, totalBlogs, categories, blogSettings} =
    await useBlogCategoriesPageQuery({
      languageCode: getLangagueCodeForSanityQuery(
        context.storefront.i18n.language,
      ),
      countryCode: getCountryCodeForSanityQuery(
        context.storefront.i18n.country,
      ),
      start,
      end,
      categoryHandle: params.categoryHandle,
    });
  // invariant(params.pageHandle, 'Missing page handle');

  const seo = {
    title: context.storefront.i18n.language === 'ZH_TW' ? '網誌' : 'Blog',
    description: context.storefront.i18n.language === 'ZH_TW' ? '網誌' : 'Blog',
    url: request.url,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      title: context.storefront.i18n.language === 'ZH_TW' ? '網誌' : 'Blog',
      description:
        context.storefront.i18n.language === 'ZH_TW' ? '網誌' : 'Blog',
      url: request.url,
    },
  };

  // const seo = seoPayload.page({page, url: request.url});
  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  return json(
    {
      translation: translation[selectedLocale],

      // page,
      // seo,
      // title: pageData.title,
      // modules: pageData.modules,
      // allBrands,
      // brandsSettings,
      seo,
      blogs,
      recentBlogs,
      tags,
      totalBlogs,
      categories,
      blogSettings,
      currentTag: null,
      currentCategory,
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  // const {title} = useLoaderData<typeof loader>();
  const {pageHandle} = useParams();
  const isHydrated = useIsHydrated();
  const {
    blogs,
    tags,
    categories,
    blogSettings,
    currentTag,
    currentCategory,
    totalBlogs,
    recentBlogs,
  } = useLoaderData<typeof loader>();
  return (
    <BlogListing
      blogs={blogs}
      tags={tags}
      categories={categories}
      blogSettings={blogSettings}
      currentTag={currentTag}
      currentCategory={currentCategory}
      totalBlogs={totalBlogs}
      recentBlogs={recentBlogs}
    />
  );
}

const PAGE_QUERY = `#graphql
  query PageDetails($language: LanguageCode, $handle: String!)
  @inContext(language: $language) {
    page(handle: $handle) {
      id
      title
      body
      seo {
        description
        title
      }
    }
  }
`;
